import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Tooltip } from './tooltip';
import { ConfirmationModal } from './modal';
import { Formik, FormikProps } from 'formik';

export type ButtonType = 'impact' | 'action' | 'display' | 'reset' | 'unselected' | 'submit' | 'gradient';
export type ButtonSize = 'small' | 'medium' | 'large';

export type ButtonProps = {
    text: string;
    onClick: () => void;
    type: ButtonType;
    loading?: boolean;
    disabled?: boolean;
    tooltip?: string;
    size?: ButtonSize;
    gradientColors?: string[];
};

export const Button: React.FC<ButtonProps> = (props) => {
    const [color, setColor] = useState('bg-blue-500');
    const [hover, setHover] = useState('bg-blue-700');
    const [isGradient, setIsGradient] = useState(false);
    const [gradientStyle, setGradientStyle] = useState({});
    const [animatedGradient, setAnimatedGradient] = useState(false);

    const [size] = useState<ButtonSize>(props.size ?? 'medium');

    useEffect(() => {
        if (props.disabled) {
            setColor('bg-gray-400');
            setHover('bg-gray-400');
            setIsGradient(false);
            return;
        }
        
        if (props.type === 'gradient') {
            setIsGradient(true);
            
            const color1 = props.gradientColors?.[0] || '#ff0000';
            const color2 = props.gradientColors?.[1] || '#ff8c00';
            
            setGradientStyle({
                backgroundSize: '200% 200%',
                backgroundImage: `linear-gradient(to right, ${color1}, ${color2}, ${color1})`,
                animation: 'gradientShift 3s ease infinite',
            });
            
            // Add the animation keyframes to the document if not already added
            if (!animatedGradient) {
                const styleSheet = document.createElement('style');
                styleSheet.innerHTML = `
                    @keyframes gradientShift {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                `;
                document.head.appendChild(styleSheet);
                setAnimatedGradient(true);
            }
            
            // Clear bg classes when using gradient
            setColor('');
            setHover('');
            return;
        }
        
        setIsGradient(false);
        
        switch (props.type) {
            case 'action':
            case 'submit':
                setColor('bg-orange-500');
                setHover('bg-orange-700');
                break;
            case 'unselected':
                setColor('bg-orange-800');
                setHover('bg-orange-800');
                break;
            case 'display':
                setColor('bg-neutral-500');
                setHover('bg-neutral-700');
                break;
            case 'reset':
                setColor('bg-red-500');
                setHover('bg-red-700');
                break;
            case 'impact':
                setColor('bg-amber-500');
                setHover('bg-amber-700');
                break;
            default:
                setColor('bg-blue-500');
                setHover('bg-blue-700');
        }
    }, [props.type, props.disabled, props.gradientColors]);

    const buttonClass = isGradient 
        ? `text-white font-bold py-2 px-8 rounded-full transition-transform duration-200 hover:scale-105`
        : `text-white font-bold py-2 px-4 rounded-md hover:${hover} ${color}`;

    return (
        <Tooltip text={props.tooltip ?? ''}>
            <button
                className={buttonClass}
                onClick={props.onClick}
                type="button"
                disabled={props.disabled}
                title={props.tooltip}
                style={isGradient ? gradientStyle : {}}
            >
                <span className={`flex items-center justify-center ${size === 'small' ? 'text-sm' : size === 'medium' ? 'text-base' : 'text-lg'}`}>
                    {props.loading ? <Spinner /> : props.text }
                </span>
            </button>
        </Tooltip>
    );
};

export const Spinner = () => {
    return (
        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    );
};

export type Web3ButtonProps<T> = Omit<ButtonProps, 'onClick' | 'loading' | 'type'> & {
    web3Action: () => Promise<T>;
    onSuccess?: (t?: T) => void;
    type?: ButtonType;
    disabled?: boolean;
    tooltip?: string;
    size?: ButtonSize;
    gradientColors?: string[];
};

export const Web3Button = <T,>(props: Web3ButtonProps<T>) => {
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleConfirmation = () => {
        setIsModalOpen(true);
    };

    const setLoadingState = (loading: boolean) => {
        setLoading(loading);
        setDisabled(loading);
    }

    const handleAction = async () => {
        setIsModalOpen(false);
        setLoadingState(true);
        try {
            const data = await props.web3Action();
            if (props.onSuccess) {
                props.onSuccess(data);
            }
        } catch (err: any) {
            toast.error(err.reason);
        }
        setLoadingState(false);
    };

    return (
        <>
            <Button 
                text={props.text} 
                onClick={handleConfirmation} 
                type={props.type ?? 'action'} 
                loading={loading} 
                disabled={props.disabled ?? disabled} 
                tooltip={props.tooltip} 
                size={props.size}
                gradientColors={props.gradientColors}
            />
            <ConfirmationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Confirmation" onConfirm={handleAction}>
                <p>Etes vous sur de vouloir effectuer cette action ?</p>
            </ConfirmationModal>
        </>
    );
};